/**
 * Qliro Analytics - Minimal Payment Tracking
 * 
 * Tracks ONLY:
 * - Payment process started
 * - Payment method used
 */

const QLIRO_ANALYTICS_ENABLED = true;

class QliroAnalytics {
  constructor() {
    this.paymentMethod = null;
    this.isInitialized = false;
    
    // API endpoint - via Shopify app proxy
    this.apiEndpoint = '/apps/proxy/qliro-analytics/track';
    
    // Circuit breaker - stop after 1 failed attempt
    this.backendAvailable = true;
  }

  /**
   * Initialize analytics tracking
   */
  initialize() {
    if (!QLIRO_ANALYTICS_ENABLED) {
      return;
    }
    
    if (this.isInitialized) return;

    this.isInitialized = true;
    
    // Setup q1Ready callback
    const self = this;
    window.q1Ready = function(q1) {
      self.onQliroReady(q1);
    };
  }

  /**
   * Called when Qliro checkout is ready
   */
  onQliroReady(q1) {
    try {
      this.registerEventListeners(q1);
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Register minimal event listeners
   */
  registerEventListeners(q1) {
    // 1. Capture payment method (don't send event)
    try {
      q1.onPaymentMethodChanged((paymentMethod) => {
        try {
          if (paymentMethod.method) {
            this.paymentMethod = paymentMethod.method;

            // GTM: add_payment_info (GA4 standard) + custom event
            if (window.dataLayer) {
              dataLayer.push({ ecommerce: null });
              dataLayer.push({
                event: 'add_payment_info',
                ecommerce: {
                  currency: window.Shopify?.currency?.active || 'SEK',
                  payment_type: paymentMethod.method
                }
              });
              dataLayer.push({ event: 'custom_qliro_payment_method_changed', payment_method: paymentMethod.method });
            }
          }
        } catch (e) {
          // Silent fail
        }
      });
    } catch (e) {
      // Silent fail
    }

    // 2. ONLY EVENT: Send when payment process starts
    try {
      q1.onPaymentProcess(
        () => {
          try {

            // Set global processing flag to prevent navigation during payment
            window.qliroPaymentProcessing = true;
            // Push history entry so back button can be intercepted
            history.pushState(null, '', window.location.href);


            this.trackPaymentStarted();

            // GTM: begin_checkout (payment process starting = checkout initiated)
            if (window.dataLayer) {
              dataLayer.push({ ecommerce: null });
              dataLayer.push({
                event: 'begin_checkout',
                ecommerce: {
                  currency: window.Shopify?.currency?.active || 'SEK'
                }
              });

              // GTM: add_payment_info
              dataLayer.push({ ecommerce: null });
              dataLayer.push({
                event: 'add_payment_info',
                ecommerce: {
                  currency: window.Shopify?.currency?.active || 'SEK',
                  payment_type: this.paymentMethod || 'default'
                }
              });
            }
          } catch (e) {
            // Silent fail
          }
        },
        () => {

          // Payment ended (failed/cancelled) - clear processing flag
          window.qliroPaymentProcessing = false;

        }
      );
    } catch (e) {
      // Silent fail
    }
  }

  /**
   * Track payment process started
   */
  async trackPaymentStarted() {
    let cartToken = this.getCartToken();
    
    // If no token in cache, fetch from API as last resort
    if (!cartToken) {
      cartToken = await this.fetchCartToken();
      
      if (!cartToken) {
        return;
      }
    }
    
    const eventData = {
      event: 'payment_process_started',
      checkoutToken: cartToken,
      paymentMethod: this.paymentMethod,
      timestamp: new Date().toISOString()
    };
    
    this.sendEvent(eventData);
  }

  /**
   * Send event to backend using ApiService
   */
  async sendEvent(eventData) {
    if (!QLIRO_ANALYTICS_ENABLED) {
      return;
    }
    
    if (!this.backendAvailable) {
      return;
    }
    
    try {
      // Use ApiService if available
      if (window.apiService && typeof window.apiService.post === 'function') {
        await window.apiService.post(this.apiEndpoint, eventData);
      } else {
        this.backendAvailable = false;
      }
    } catch (error) {
      // Stop trying after first failure
      this.backendAvailable = false;
    }
  }

  /**
   * Get cart token from Shopify (synchronous - uses cached value)
   */
  getCartToken() {
    // Try CartQliroManager (set by cart.js)
    if (window.cartQliroManager && window.cartQliroManager.currentCartToken) {
      return window.cartQliroManager.currentCartToken;
    }
    
    // Try window.cart (global cart object updated by multiple places)
    if (window.cart && window.cart.token) {
      return window.cart.token;
    }
    
    // Try window.cartData
    if (window.cartData && window.cartData.token) {
      return window.cartData.token;
    }
    
    // Try Shopify global
    if (window.Shopify && window.Shopify.cartToken) {
      return window.Shopify.cartToken;
    }
    
    return null;
  }

  /**
   * Fetch cart token from Shopify API
   */
  async fetchCartToken() {
    try {
      if (window.apiService && typeof window.apiService.get === 'function') {
        let cart = await window.apiService.get('/cart.js');
        
        // ApiService sometimes returns JSON as string, parse if needed
        if (typeof cart === 'string') {
          cart = JSON.parse(cart);
        }
        
        return cart.token;
      } else {
        // Fallback to fetch
        const response = await fetch('/cart.js');
        const cart = await response.json();
        if (typeof window.syncKnownCartStateFromCart === 'function') {
          window.syncKnownCartStateFromCart(cart);
        } else if (typeof window.updateKnownCartState === 'function') {
          window.updateKnownCartState(cart);
        } else {
          const itemCount = Number(cart?.item_count || 0);
          const itemLength = Array.isArray(cart?.items) ? cart.items.length : 0;
          const cartStateMeta = typeof window.buildCartStateMeta === 'function'
            ? window.buildCartStateMeta()
            : { updated_at: new Date().toISOString(), updated_at_ms: Date.now() };

          window.cart = cart;
          window.__cartState = {
            isLoaded: true,
            itemCount,
            isEmpty: itemCount === 0 && itemLength === 0,
            ...cartStateMeta
          };
        }
        return cart.token;
      }
    } catch (error) {
      return null;
    }
  }
}

// ========== Auto-Initialize ==========

// Attach analytics to window for cart.js to call
window.qliroAnalytics = new QliroAnalytics();

// Always initialize immediately - Qliro will call q1Ready when it's loaded
window.qliroAnalytics.initialize();


// Intercept link clicks to prevent navigation while payment is processing
document.addEventListener('click', (e) => {
  if (!window.qliroPaymentProcessing) return;
  const link = e.target.closest('a[href]');
  if (!link) return;
  const href = link.getAttribute('href');
  if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;
  e.preventDefault();
  e.stopPropagation();
}, true);

// Prevent browser back button during payment processing
window.addEventListener('popstate', () => {
  if (window.qliroPaymentProcessing) {
    history.pushState(null, '', window.location.href);
  }
});

